import { useTranslation } from 'react-i18next';

import { SWReactIcons } from '@similarweb/icons';

import { useAppState } from '../../../AppStateProvider';
import { Modal } from '../../../components/Modal';
import { Tooltip } from '../../../components/Tooltip';
import { useContactsCountByCountriesQuery } from '../../../features/contacts/companyContactList/useContactsCountByCountriesQuery';

import { ContactDetailsFilter } from './ContactDetailsFilter';
import { ContactStatusFilter } from './ContactStatusFilter';
import { CountryFilter } from './CountryFilter';
import { StyledBadge, StyledOpenFiltersButton } from './styles';
import { ContactFilter, useContactFilter } from './useContactFilter';

export const ContactFilters = ({ contactsNumber }: { contactsNumber: number }) => {
  const { t } = useTranslation();
  const { contactFilters } = useAppState();
  const { isLoading, data: contactsCountByCountries = [] } = useContactsCountByCountriesQuery();

  const {
    isFiltersModalOpen,
    setIsFiltersModalOpen,
    openedFilter,
    toggleFilter,
    selectedCountries,
    setSelectedCountries,
    selectedContactDetails,
    contactStatus,
    setContactStatus,
    handleClickOnDetails,
    appliedFiltersCount,
    handleApplyFilters,
    handleClickOnClearFilters,
    trackFilterEvents,
    resetFilters,
  } = useContactFilter({ contactsNumber });

  const handleOpenFilters = () => {
    trackFilterEvents('open filters panel');
    setIsFiltersModalOpen(true);
  };

  return (
    <>
      <Tooltip content={t('contacts.filters.more_filters')} placement="top" data-automation="ContactsFilterTooltip">
        <StyledOpenFiltersButton onClick={handleOpenFilters} disabled={isLoading}>
          <SWReactIcons iconName="toggle-filters" size="xs" />
          {appliedFiltersCount > 0 ? <StyledBadge>{appliedFiltersCount}</StyledBadge> : null}
        </StyledOpenFiltersButton>
      </Tooltip>
      <Modal
        isOpen={isFiltersModalOpen}
        onClose={() => setIsFiltersModalOpen(false)}
        heading={t('contacts.filters')}
        cancelButtonProps={{
          children: t('contacts.filters.clear_all'),
          variant: 'flat',
          onClick: handleClickOnClearFilters,
          disabled: appliedFiltersCount === 0,
        }}
        confirmButtonProps={{
          children: t('contacts.filters.show_results'),
          variant: 'primary',
          onClick: handleApplyFilters,
        }}
        footerJustifyContent="space-between"
        contentHorizontalPadding={0}
        onCloseFinish={resetFilters}
      >
        <CountryFilter
          isOpen={openedFilter === ContactFilter.countries}
          onToggle={() => toggleFilter(ContactFilter.countries)}
          selectedCountries={selectedCountries}
          onChange={setSelectedCountries}
          contactsCountByCountries={contactsCountByCountries}
          initiallySelectedCountries={contactFilters.includeCountries}
        />
        <ContactDetailsFilter
          isOpen={openedFilter === ContactFilter.contactDetails}
          onToggle={() => toggleFilter(ContactFilter.contactDetails)}
          selectedContactDetails={selectedContactDetails}
          handleClickOnDetails={handleClickOnDetails}
        />
        <ContactStatusFilter
          isOpen={openedFilter === ContactFilter.status}
          onToggle={() => toggleFilter(ContactFilter.status)}
          value={contactStatus}
          onChange={setContactStatus}
        />
      </Modal>
    </>
  );
};
